import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Trash2, Plus, Loader2 } from 'lucide-react';
import { WebhookTableRow } from './WebhookTableRow';
import { WebhooksTableProps } from './types';
import { useWebhooks } from './useWebhooks';
import { ConfigurationProgress } from './ConfigurationProgress';
import { platformsData } from './platformsData';
import { WebhookEditModal } from './WebhookEditModal';
import { getStatusLabel } from './types';
import { useTrackingData } from './useTrackingData';

export function WebhooksTable({ onTabChange, savedWebhooks = [], onWebhooksSaved, activeTab, setActiveTab, onBack }: WebhooksTableProps) {
  useLanguage();
  const { data: trackingData, isLoading } = useTrackingData();
  const {
    webhooks,
    videos,
    selectedWebhooks,
    handleCopyUrl,
    handleDeleteWebhook,
    handleTokenChange,
    handleVideoChange,
    handleSaveTracking,
    handleToggleSelect,
    handleSelectAll,
    handleClearSelection,
    handleDeleteSelected,
  } = useWebhooks(onTabChange, savedWebhooks, onWebhooksSaved);

  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const allSelected = selectedWebhooks.length === webhooks.length && webhooks.length > 0;

  // Calculate configuration progress
  const configuredCount = webhooks.filter(w => w.status === 'waiting' || w.status === 'active').length;
  const totalCount = webhooks.length;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-3 text-gray-500">{translateInterfaceText("Carregando dados...")}</p>
      </div>
    );
  }

  if (webhooks.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{translateInterfaceText("Nenhuma plataforma selecionada. Adicione plataformas para visualizar os webhooks.")}</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Configuration Progress Indicator */}
      <ConfigurationProgress total={totalCount} configured={configuredCount} />

      {/* Barra de Ação Flutuante */}
      {selectedWebhooks.length > 0 && (
        <div className="sticky top-0 z-10 bg-primary/10 border border-primary/20 rounded-lg px-4 py-3 flex items-center justify-between shadow-sm">
          <span className="text-sm font-medium">
            {selectedWebhooks.length} {selectedWebhooks.length === 1 ? translateInterfaceText("selecionado") : translateInterfaceText("selecionados")}
          </span>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleClearSelection}
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowBulkDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {translateInterfaceText("Excluir")}</Button>
          </div>
        </div>
      )}

      {/* Desktop: Card da Tabela Premium */}
      <div className="hidden sm:block bg-white border border-border rounded-xl shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAFAFA] border-b border-border hover:bg-[#FAFAFA] h-12">
                <TableHead className="w-12 font-semibold text-[#4B5563]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[140px] font-semibold text-[#4B5563]">{translateInterfaceText("Plataforma")}</TableHead>
                <TableHead className="font-semibold text-[#4B5563]">{translateInterfaceText("URL Webhook")}</TableHead>
                <TableHead className="w-[180px] font-semibold text-[#4B5563]">{translateInterfaceText("Vídeo")}</TableHead>
                <TableHead className="w-[180px] font-semibold text-[#4B5563]">{translateInterfaceText("Token")}</TableHead>
                <TableHead className="w-[160px] font-semibold text-[#4B5563]">{translateInterfaceText("Status")}</TableHead>
                <TableHead className="w-[180px] font-semibold text-[#4B5563]">{translateInterfaceText("Ações")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map((webhook) => (
                <WebhookTableRow
                  key={webhook.id}
                  webhook={webhook}
                  videos={videos}
                  allWebhooks={webhooks}
                  isSelected={selectedWebhooks.includes(webhook.id)}
                  onToggleSelect={handleToggleSelect}
                  onCopy={handleCopyUrl}
                  onDelete={handleDeleteWebhook}
                  onEdit={(id) => setEditingWebhookId(id)}
                  onVideoChange={handleVideoChange}
                  onTokenChange={handleTokenChange}
                  onSaveTracking={handleSaveTracking}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile: Cards empilhados */}
      <div className="sm:hidden space-y-2.5 pb-[120px]">
        {webhooks.map((webhook) => {
          const platformData = platformsData.find(p => p.id === webhook.platform);
          const isSelected = selectedWebhooks.includes(webhook.id);
          const statusInfo = getStatusLabel(webhook.status);
          const isEditable = webhook.status === 'creation';
          
          return (
            <div 
              key={webhook.id} 
              className={`bg-white border rounded-xl p-3 transition-all ${
                isSelected ? 'border-[#2563EB] shadow-[0_2px_8px_rgba(37,99,235,0.15)]' : 'border-[#E7EAF2]'
              } ${isEditable ? 'cursor-pointer active:bg-gray-50' : ''}`}
              onClick={() => {
                if (isEditable) {
                  setEditingWebhookId(webhook.id);
                }
              }}
            >
              <div className="flex items-center gap-2.5">
                <Checkbox 
                  checked={isSelected}
                  onCheckedChange={() => handleToggleSelect(webhook.id)}
                  className="w-4 h-4 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex items-center justify-center shrink-0">
                  <img
                    src={platformData?.logo}
                    alt={platformData?.name}
                    className="h-5 w-5 sm:h-7 sm:w-auto sm:max-w-[100px] object-contain"
                  />
                </div>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <p
                      className="text-[12px] text-[#6B7280] truncate"
                      title={webhook.status !== 'creation' ? webhook.url : ''}
                    >
                      {webhook.status === 'creation' ? translateInterfaceText("Salve o tracking para liberar a URL") : webhook.url}
                    </p>
                    <p className="text-[13px] text-[#374151] font-medium truncate">
                      {webhook.selectedVideoId ?
                        videos.find(v => v.id === webhook.selectedVideoId)?.title || translateInterfaceText("Vídeo não encontrado")
                        : translateInterfaceText("Nenhum vídeo selecionado")
                      }
                    </p>
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md border whitespace-nowrap ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile Edit Sheet */}
      <WebhookEditModal
        webhook={webhooks.find(w => w.id === editingWebhookId) || null}
        open={editingWebhookId !== null}
        onOpenChange={(open) => !open && setEditingWebhookId(null)}
        videos={videos}
        onVideoChange={(videoId) => {
          if (editingWebhookId) {
            handleVideoChange(editingWebhookId, videoId);
          }
        }}
        onTokenChange={(token) => {
          if (editingWebhookId) {
            handleTokenChange(editingWebhookId, token);
          }
        }}
        onCopyUrl={() => {
          const webhook = webhooks.find(w => w.id === editingWebhookId);
          if (webhook) {
            handleCopyUrl(webhook.url);
          }
        }}
        onSave={() => {
          if (editingWebhookId) {
            handleSaveTracking(editingWebhookId);
            setEditingWebhookId(null);
          }
        }}
      />

      {/* Modal de Exclusão em Massa */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent className="w-[90vw] w-[580px] rounded-xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-semibold text-foreground">
              {translateInterfaceText("Excluir")}{" "}{selectedWebhooks.length} {" "}{translateInterfaceText("tracking(s)?")}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground mt-2">
              {translateInterfaceText("Tem certeza que deseja excluir os")}{" "}{selectedWebhooks.length} {" "}{translateInterfaceText("trackings selecionados? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6 flex justify-end gap-3">
            <AlertDialogCancel className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#1E293B] transition-colors">
              {translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowBulkDeleteDialog(false);
              }}
              className="h-11 px-5 rounded-[10px] bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors"
            >
              {translateInterfaceText("Excluir Todos")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
